// src/scenes/vehicles/components/VehicleDetails.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, useTheme, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, useMediaQuery } from "@mui/material";
import { DataGrid, esES } from "@mui/x-data-grid";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../../components/Header";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import SpeedIcon from "@mui/icons-material/Speed";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import { getVehicleById, updateVehicle } from "../../../services/vehicleService";
import { tokens } from "../../../theme";

/* ===== Paletas ===== */
const PALETTE_DARK = {
  cardBg: "#1E293B",
  cardBorder: "#334155",
  text: "#F1F5F9",
  label: "#94A3B8",
  title: "#E2E8F0",
  accent: "#38BDF8",
  btn: "#3B82F6",
  btnHover: "#2563EB",
};
const PALETTE_LIGHT = {
  cardBg: "#ECECEC",
  cardBorder: "#E5E7EB",
  text: "#1E293B",
  label: "#475569",
  title: "#111827",
  accent: "#38BDF8",
  btn: "#2563EB",
  btnHover: "#1D4ED8",
};

/* ===== Widget KPI ===== */
function StatCard({ icon, label, value, palette, highlight = false }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: "12px",
        backgroundColor: palette.cardBg,
        border: `1px solid ${highlight ? palette.accent : palette.cardBorder}`,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        minHeight: 76,
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 6px 18px rgba(2,6,23,0.08)"
            : "0 2px 6px rgba(0,0,0,0.20)",
      }}
    >
      <Box sx={{ opacity: 0.95, display: "grid", placeItems: "center" }}>
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: palette.label,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontWeight: 600,
          }}
        >
          {label}
        </Typography>
        <Typography variant="h6" sx={{ color: palette.text, fontWeight: 800 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

/* ===== Card contenedora ===== */
function Card({ title, palette, children, action }) {
  return (
    <Box
      sx={{
        backgroundColor: palette.cardBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: "14px",
        p: 1.5,
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        boxShadow:
          palette === PALETTE_LIGHT
            ? "0 8px 22px rgba(2,6,23,0.08)"
            : "0 3px 10px rgba(0,0,0,0.24)",
        overflow: "hidden",
        width: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: action ? "space-between" : "flex-start",
          gap: 1,
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ color: palette.title, fontWeight: 800 }}>
          {title}
        </Typography>
        {action && <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{action}</Box>}
      </Box>
      <Box sx={{ flex: 1, minHeight: 0, width: "100%", display: "flex" }}>
        {children}
      </Box>
    </Box>
  );
}

/* ===== Estilo DataGrid ===== */
const dgSx = (palette) => ({
  height: "100%",
  width: "100%",
  display: "flex",
  flex: 1,
  "& .MuiDataGrid-root": {
    border: "none",
    color: palette.text,
    backgroundColor: "transparent",
    height: "100%",
    width: "100%",
  },
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: "transparent",
    borderBottom: `1px solid ${palette.cardBorder}`,
    color: palette.label,
    fontWeight: 700,
  },
  "& .MuiDataGrid-cell": { borderBottom: "none" },
  "& .MuiDataGrid-virtualScroller": { backgroundColor: "transparent" },
  "& .MuiDataGrid-footerContainer": {
    borderTop: `1px solid ${palette.cardBorder}`,
    backgroundColor: "transparent",
  },
  "& .MuiDataGrid-overlay": {
    backgroundColor: "transparent",
    color: palette.label,
  },
});

/* ===== Página ===== */
const VehicleDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const palette = useMemo(
    () => (theme.palette.mode === "dark" ? PALETTE_DARK : PALETTE_LIGHT),
    [theme.palette.mode]
  );
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const modalCardBg = isDark ? "#1e2a40" : "#fdfcfc";
  const modalBorder = isDark ? "#2b3b55" : "#d1d5db";
  const modalInputBg = isDark ? colors.primary[500] : "#ececec";
  const modalText = isDark ? colors.grey[100] : "#000000";
  const modalLabel = isDark ? "#ffffff" : "#000000";
  const modalAccent = colors.blueAccent[400];
  const modalInputStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: modalInputBg,
      color: modalText,
      "& fieldset": { borderColor: modalBorder },
      "&:hover fieldset": { borderColor: modalBorder },
      "&.Mui-focused fieldset": { borderColor: modalAccent, borderWidth: 1.5 },
    },
    "& .MuiInputBase-input": { color: modalText },
    "& .MuiInputLabel-root": { color: modalLabel },
    "& .MuiInputLabel-root.Mui-focused": { color: modalText },
  };

  const [vehicle, setVehicle] = useState(null);
  const [maintenances] = useState([]);
  const [turnos] = useState([]);
  const [loadingVehicle, setLoadingVehicle] = useState(true);
  const [loadingMaint, setLoadingMaint] = useState(true);
  const [loadingTurnos, setLoadingTurnos] = useState(true);
  const [error, setError] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    brand: "",
    model: "",
    year: "",
    kilometres: "",
    licensePlate: "",
  });

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        setLoadingVehicle(true);
        setError(null);
        const data = await getVehicleById(id);
        setVehicle(data);
        setEditForm({
          brand: data?.brand || "",
          model: data?.model || "",
          year: data?.year || "",
          kilometres: data?.kilometres ?? "",
          licensePlate: data?.licensePlate || "",
        });
      } catch (err) {
        console.error("Error al cargar vehículo:", err);
        setError(err?.message || "No se pudo cargar el vehículo.");
        setVehicle(null);
      } finally {
        setLoadingVehicle(false);
        setLoadingMaint(false);
        setLoadingTurnos(false);
      }
    };

    fetchVehicle();
  }, [id]);

  const antiguedad = vehicle?.year
    ? Math.max(0, new Date().getFullYear() - Number(vehicle.year))
    : "—";
  const driverName =
    vehicle?.driver && (vehicle.driver.name || vehicle.driver.lastName)
      ? `${vehicle.driver.name ?? ""} ${vehicle.driver.lastName ?? ""}`.trim()
      : "—";

  const handleOpenEdit = () => setEditOpen(true);
  const handleCloseEdit = () => setEditOpen(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const numeric = name === "year" || name === "kilometres";
    setEditForm((prev) => ({
      ...prev,
      [name]: numeric ? (value === "" ? "" : Number(value)) : value,
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const payload = {
        brand: editForm.brand,
        model: editForm.model,
        year: editForm.year || null,
        kilometres: editForm.kilometres !== "" ? editForm.kilometres : null,
        licensePlate: editForm.licensePlate,
      };
      const updated = await updateVehicle(vehicle._id, payload);
      setVehicle(updated);
      setEditOpen(false);
    } catch (err) {
      setError(err?.message || "No se pudo actualizar el vehículo.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingVehicle) {
    return (
    <Box
      sx={{
        m: "20px",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1,
      }}
    >
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 500,
            alignSelf: "flex-start",
            mb: 1,
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Volver
        </Button>
        <Header title="Detalle del Vehículo" subtitle="Información y actividad reciente" />
        <Typography sx={{ color: palette.label, mt: 2 }}>Cargando vehículo...</Typography>
      </Box>
    );
  }

  if (!vehicle) {
    return (
      <Box
        sx={{
          m: "20px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 0,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{
            color: palette.title,
            textTransform: "none",
            fontSize: "16px",
            fontWeight: 500,
            alignSelf: "flex-start",
            mb: 1,
            "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
          }}
        >
          Volver
        </Button>
        <Header title="Detalle del Vehículo" subtitle="Información y actividad reciente" />
        <Typography sx={{ color: palette.accent, mt: 2 }}>
          {error || "No se ha encontrado el vehículo solicitado."}
        </Typography>
      </Box>
    );
  }

  const colsMaint = [
    { field: "title", headerName: "Mantenimiento", flex: 1 },
    { field: "date", headerName: "Fecha", flex: 1 },
    { field: "cost", headerName: "Coste (€)", flex: 1 },
    { field: "status", headerName: "Estado", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/maintenance/${params.row.id}`)}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
          }}
        >
          Ver detalles
        </Button>
      ),
    },
  ];

  const colsTurnos = [
    { field: "date", headerName: "Fecha", flex: 1 },
    { field: "driver", headerName: "Conductor", flex: 1 },
    { field: "startKm", headerName: "Km Inicio", flex: 1 },
    { field: "endKm", headerName: "Km Fin", flex: 1 },
    { field: "income", headerName: "Recaudación (€)", flex: 1 },
    {
      field: "acciones",
      headerName: "Acciones",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Button
          variant="contained"
          size="small"
          onClick={() => navigate(`/turnos/${params.row.id}`)}
          sx={{
            backgroundColor: palette.btn,
            color: "white",
            fontWeight: 700,
            textTransform: "none",
            "&:hover": { backgroundColor: palette.btnHover },
          }}
        >
          Ver detalles
        </Button>
      ),
    },
  ];

  return (
    <>
      <Box
        sx={{
        m: "20px",
        display: "flex",
        flexDirection: "column",
        height: isSmallScreen ? "auto" : "100%",
        minHeight: isSmallScreen ? "100%" : 0,
        gap: 1,
        pb: isSmallScreen ? 2 : 0,
      }}
    >
      {/* Botón volver */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{
          color: palette.title,
          textTransform: "none",
          fontSize: "16px",
          fontWeight: 500,
          alignSelf: "flex-start",
          mb: 1,
          "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
        }}
      >
        Volver
      </Button>

      <Header title="Detalle del Vehículo" subtitle="Información y actividad reciente" />

      {/* KPIs */}
      <Grid container spacing={2} sx={{ mt: 1, mb: 1 }}>
        <Grid item xs={12} md={3}>
          <StatCard
            icon={<DirectionsCarIcon />}
            label="Modelo"
            value={[vehicle?.brand, vehicle?.model].filter(Boolean).join(" ") || "—"}
            palette={palette}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={<PersonIcon />} label="Conductor" value={driverName} palette={palette} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={<ConfirmationNumberIcon />} label="Matrícula" value={vehicle?.licensePlate ?? "—"} palette={palette} />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={<SpeedIcon />} label="Km" value={(vehicle?.kilometres ?? 0).toLocaleString("es-ES")} palette={palette} highlight />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard icon={<CalendarMonthIcon />} label="Antigüedad" value={`${antiguedad} años`} palette={palette} />
        </Grid>
        <Grid item xs={12} md={3}>
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "flex-start",
            }}
          >
            <Button
              variant="contained"
              onClick={handleOpenEdit}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                textTransform: "none",
                fontWeight: 700,
                width: { xs: "100%", md: "auto" },
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Editar vehículo
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Dos tarjetas lado a lado */}
      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: isSmallScreen ? "auto" : 0,
          height: isSmallScreen ? "auto" : "100%",
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
          gridAutoRows: { xs: "minmax(340px, auto)", lg: "1fr" },
          columnGap: 2,
          rowGap: { xs: 2, lg: 0 },
          alignItems: "stretch",
        }}
      >
        <Card
          title="Mantenimientos"
          palette={palette}
          action={
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate("/maintenance/register")}
              sx={{
                backgroundColor: palette.btn,
                color: "white",
                textTransform: "none",
                fontWeight: 700,
                "&:hover": { backgroundColor: palette.btnHover },
              }}
            >
              Añadir mantenimiento
            </Button>
          }
        >
          <Box
            sx={{
              ...dgSx(palette),
              height: isSmallScreen ? "auto" : "100%",
              minHeight: isSmallScreen ? 320 : undefined,
            }}
          >
            <DataGrid
              rows={maintenances}
              columns={colsMaint}
              loading={loadingMaint}
              getRowId={(r) => r.id}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              rowHeight={52}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              autoHeight={isSmallScreen}
              sx={{ height: isSmallScreen ? "auto" : "100%", width: "100%" }}
            />
          </Box>
        </Card>

        <Card title="Últimos turnos del vehículo" palette={palette}>
          <Box
            sx={{
              ...dgSx(palette),
              height: isSmallScreen ? "auto" : "100%",
              minHeight: isSmallScreen ? 320 : undefined,
            }}
          >
            <DataGrid
              rows={turnos}
              columns={colsTurnos}
              loading={loadingTurnos}
              getRowId={(r) => r.id}
              pageSizeOptions={[5, 10, 25]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              localeText={esES.components.MuiDataGrid.defaultProps.localeText}
              rowHeight={52}
              columnHeaderHeight={48}
              disableRowSelectionOnClick
              autoHeight={isSmallScreen}
              sx={{ height: isSmallScreen ? "auto" : "100%", width: "100%" }}
            />
          </Box>
        </Card>
      </Box>
      </Box>

      <Dialog
        open={editOpen}
        onClose={handleCloseEdit}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: modalCardBg,
            border: `1px solid ${modalBorder}`,
            color: modalText,
            boxShadow:
              theme.palette.mode === "light"
                ? "0 10px 30px rgba(15,23,42,0.18)"
                : "0 14px 40px rgba(0,0,0,0.45)",
          },
        }}
      >
        <DialogTitle sx={{ color: modalText, fontWeight: 700 }}>
          Editar vehículo
        </DialogTitle>
        <DialogContent dividers sx={{ borderColor: modalBorder }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleSave();
            }}
            display="grid"
            gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
            gap={2}
            pt={0.5}
          >
            <TextField
              label="Marca"
              name="brand"
              value={editForm.brand}
              onChange={handleChange}
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Modelo"
              name="model"
              value={editForm.model}
              onChange={handleChange}
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Año"
              name="year"
              type="number"
              value={editForm.year}
              onChange={handleChange}
              inputProps={{ min: 1900, max: 2100, step: 1 }}
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Kilómetros"
              name="kilometres"
              type="number"
              value={editForm.kilometres}
              onChange={handleChange}
              inputProps={{ min: 0, step: 100 }}
              fullWidth
              sx={modalInputStyles}
            />
            <TextField
              label="Matrícula"
              name="licensePlate"
              value={editForm.licensePlate}
              onChange={handleChange}
              fullWidth
              sx={modalInputStyles}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit} disabled={saving} sx={{ color: modalText }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: "white",
              textTransform: "none",
              "&:hover": { backgroundColor: colors.blueAccent[400] },
            }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VehicleDetails;
